import { ActivityIndicator, DeviceEventEmitter, KeyboardAvoidingView, RefreshControl, TouchableOpacity, useWindowDimensions, View, VirtualizedList } from 'react-native'
import React, { useEffect, useState } from 'react'
import { ThemeConsumer } from '@rneui/themed'
import { config } from '../../../shared/environment'
import Popover from 'react-native-popover-view'

import { CategoryProductItem } from '../categoryModule'
import { useAppNavigation, useAppRoute } from '../../../types/@react-navigation/navigation'
import { getScreenPermission, useAppDispatch, useAppSelector } from '../../../redux/hooks'
import { CategoryProductPayload, deleteCategoryProduct, getMappedCategoryProducts } from '../../../redux/slices/categorySlice'
import { useFocusEffect } from '@react-navigation/native'

import { appColors, appFonts, fontSize } from '../../../themes/appTheme'
import CommonDialogBox from '../../../shared/commonComponents/commonDialogBox'
import CommonHeader from '../../../shared/commonComponents/commonHeader'
import { CustomModal } from '../../../shared/commonComponents/customModal'

import { ZenButton, ZenCheckBox, ZenEmptyComponent, ZenIcon, ZenLoader, ZenText, ZenToast } from 'zen-core-elements'
import { CategoryListStyle } from '../../../themes/components/category/categoryListStyle'
import { SortOption } from '../../../shared/commonComponents/commonSortModal'
import { ListToolbar } from '../../../shared/commonComponents/listToolbar'
import { shallowEqual } from 'react-redux'

const Product = () => {
    let limit = 10
    const dispatch = useAppDispatch()
    const { categoryId, isView, isAllProduct, formikValues } = useAppRoute('CategoryProducts').params ?? {}
    const { loading: loadPage, value: listOfProducts, count: totalCount } = useAppSelector((state) => state?.category?.categoryProductResponse)
    const navigation = useAppNavigation()
    const { isLandscape } = useAppSelector(state => state?.common)
    const { width } = useWindowDimensions()
    const [offset, setOffset] = useState<number | null>(null)
    const [search, setSearch] = useState<string | null>(null)
    const [refresh, setRefresh] = useState<boolean>(false)
    const [footerLoader, setFooterLoader] = useState(false)
    const isAddLoading = useAppSelector((state) => state?.category?.addEditCategoryLoader)
    //variable to store the selected sort
    const [sortOrder, setSortOrder] = useState<{ field: string, order: string, id: number } | null>(null)
    const [deletePopover, setDeletePopover] = useState<boolean>(false)
    const [deleteId, setDeleteId] = useState<number | null>(null);
    const alreadyFetched = React.useRef(false);
    const [selectedProductCard, setSelectedProductCard] = useState<Set<number>>(new Set([]))
    const [isBack, setIsBack] = useState(false);
    const sortOptionsData: SortOption[] = [
        { name: 'Product name A-Z', id: 1, field: 'name', order: 'ASC' },
        { name: 'Product name Z-A', id: 2, field: 'name', order: 'DESC' },
        { name: 'Recently created first', id: 3, field: 'created_at', order: 'DESC' },
        { name: 'Recently created last', id: 4, field: 'created_at', order: 'ASC' },
        { name: 'Recently updated first', id: 5, field: 'modified_at', order: 'DESC' },
        { name: 'Recently updated last', id: 6, field: 'modified_at', order: 'ASC' }
    ]

    const reInitializeStates = () => {
        DeviceEventEmitter?.emit("productListFilter")
        setSearch(null)
        setSortOrder(null)
        setSelectedProductCard(new Set([]))
    }

    useEffect(() => {
        const productCardUpdate = DeviceEventEmitter.addListener("PRODUCTS_UPDATED", () => {
            reInitializeStates()
        })
        return () => {
            productCardUpdate?.remove()
        }
    }, [])

    // during create this useFocusEffect will be called 
    // during edit alreadyfetched turns into true,
    // in create, when add prodcuts button is clicked - isAllProduct turns true so api is called
    //when backIcon is clicked, the isAllProduct turns false so api is not called
    useFocusEffect(
        React.useCallback(() => {
            if (isAllProduct) {
                getSelectedCategoryProducts();
            } else {
                if (categoryId === 0) {
                    // if (alreadyFetched.current) {
                    getSelectedCategoryProducts();
                    // } else {
                    //     alreadyFetched.current = true;
                    // }
                } else {
                    if (!alreadyFetched.current) {
                        getSelectedCategoryProducts();
                    }
                }
            }
            setFooterLoader(false);
        }, [isAllProduct, categoryId])
    );

    const toggleOffset = () => {
        setOffset((prev) => prev != -1 ? -1 : 0)
    }

    useEffect(() => {
        if (offset !== null) {
            getSelectedCategoryProducts();
        } else { setRefresh(false) }
    }, [offset])

    useEffect(() => {
        if (search !== null) {
            const debounceSearch = setTimeout(() => {
                toggleOffset()
            }, 1000);
            return () => clearTimeout(debounceSearch)
        }
    }, [search])

    const confirmation = (item: { id: number }) => {
        if (item?.id == 2) {
            setDeletePopover(false);
            deleteCategory()
        } else {
            setDeletePopover(false)
        }
    }

    const getSelectedCategoryProducts = () => {
        let payloadData: CategoryProductPayload = {
            storeId: config?.storeId,
            data: {
                offset: offset && offset !== -1 ? offset : 0,
                limit: limit,
                searchText: search ? search?.trim() : '',
                sortOrder: sortOrder ? JSON.stringify({ field: sortOrder.field, order: sortOrder.order }) : JSON.stringify({ field: "name", order: "asc" }),
                categoryId: categoryId,
                isMapped: !isAllProduct
            },
            isAllProduct
        }
        dispatch(getMappedCategoryProducts(payloadData)).unwrap().catch((error) => {
            console.log('Error in getSelectedCategoryProducts', error);
            ZenToast({ type: 'error', message: 'Failed to get products' })
        }).finally(() => {
            setFooterLoader(false)
            setRefresh(false);
        })
    }

    const deleteCategory = () => {
        const idToDelete = selectedProductCard?.size > 0 ? Array.from(selectedProductCard) : deleteId !== null ? [deleteId] : [];
        dispatch(deleteCategoryProduct({ storeId: config?.storeId, productId: idToDelete, categoryId: categoryId })).unwrap().then((response) => {
            if (response) {
                setDeleteId(null)
                setSelectedProductCard(new Set([]))
                ZenToast({ type: 'success', message: 'Category deleted successfully' });
            }
        }).catch((err) => {
            console.log('Error in delete category', err);
            ZenToast({ type: 'error', message: 'Failed to delete category' })
        })
    }

    const toggleSelect = (id: number) => {
        setSelectedProductCard(prev => {
            const next = new Set(prev)
            next.has(id) ? next.delete(id) : next.add(id)
            return next
        })
    }
    const onLongPress = (productData: CategoryProductItem) => {
        if (!isLandscape && selectedProductCard.size === 0) {
            toggleSelect(productData.id)
        }
    }
    const onPress = (productData: CategoryProductItem) => {
        if (!isLandscape && selectedProductCard.size > 0) {
            toggleSelect(productData.id)
        }
    }

    const renderItem = ({ item, index }: { item: CategoryProductItem[]; index: number }) => {
        return (
            <ThemeConsumer>
                {({ theme }) => (
                    <View key={index} style={{ flexDirection: "row" }}>
                        {item?.map((productData: CategoryProductItem, ResponseDataIndex: number) => (
                            <View key={ResponseDataIndex} style={{ flex: 1 }}>
                                <TouchableOpacity disabled={isLandscape} onLongPress={() => !isView && onLongPress(productData)} onPress={() => !isView && onPress(productData)}                          >
                                    {/* LIST CONTAINER */}
                                    <View style={{ padding: 15, marginBottom: 10, borderRadius: 5, backgroundColor: theme.light, borderWidth: !isLandscape && selectedProductCard?.has(productData?.id) ? 1.5 : 0, borderColor: appColors.lightDark, }}                                >
                                        {/* MAIN ROW */}
                                        <View style={{ flexDirection: "row", justifyContent: "space-between" }}         >
                                            {/* LEFT SECTION */}
                                            <View style={{ flexDirection: "row", flex: 1, alignItems: "center", }}                                         >
                                                {/* CHECKBOX (landscape / width > 600) */}
                                                {(isLandscape && !isView) && (
                                                    <View style={{ width: 30 }}>
                                                        <ZenCheckBox
                                                            checked={selectedProductCard?.has(productData?.id)}
                                                            size={22}
                                                            checkedColor={appColors.primary}
                                                            onPress={() => toggleSelect(productData.id)}
                                                        />
                                                    </View>
                                                )}

                                                {/* CATEGORY NAME + BADGE */}
                                                <View style={{ flexDirection: "column", flex: 0.5, gap: 10, }}                                       >
                                                    <ZenText content={productData?.name} numberOfLines={1} style={{ fontFamily: appFonts.bold, fontSize: fontSize.mediumFontSize, color: appColors.lightDark, }} />
                                                </View>
                                            </View>

                                            {/* ACTION ICONS */}
                                            <View style={{ flexDirection: "row", alignItems: "center", gap: 10, alignSelf: "center", }}  >
                                                {(categoryId > 0 && !isAllProduct && !isView) && selectedProductCard.size <= 0 ?
                                                    <TouchableOpacity onPress={() => { setDeleteId(productData?.id), setDeletePopover(true); }}                                >
                                                        <ZenIcon
                                                            name="delete-outline"
                                                            type="material-community"
                                                            color={appColors.red}
                                                            size={theme.iconSize}
                                                            style={CategoryListStyle?.iconStyle}
                                                        />
                                                    </TouchableOpacity> : null}
                                            </View>
                                        </View>
                                    </View>
                                </TouchableOpacity>
                            </View>
                        ))}
                    </View>
                )}
            </ThemeConsumer>
        );
    };

    const getItem = (item: any[], index: number) => {
        let array: any = [];
        let numOfColumns = 1
        for (let i = 0; i < numOfColumns; i++) {
            let temp = item?.[index * numOfColumns + i];
            temp && array?.push(temp);
        }
        return array;
    };

    const footerComponent = () => {
        return (
            <ThemeConsumer>
                {({ theme }) => (
                    footerLoader ?
                        <View style={{}} >
                            <ActivityIndicator size={"small"} color={theme?.primary} />
                        </View >
                        : undefined
                )}
            </ThemeConsumer>
        )
    }

    const onEndReached = () => {
        let offsetValue = offset && offset != -1 ? offset : 0
        if (!footerLoader && (totalCount!! > offsetValue)) {
            setFooterLoader(true)
            setOffset(offsetValue + limit)
        }
    };

    const onRefresh = () => {
        setRefresh(true)
        reInitializeStates()
        toggleOffset()
    }

    return (
        <ThemeConsumer>
            {({ theme }) => (
                <KeyboardAvoidingView style={{ flex: 1, backgroundColor: "#f8f8f8", paddingBottom: 10 }}>
                    {isAllProduct && <CommonHeader headerName={"Add Products"} backIcon backIconOnpress={() => { navigation.goBack() }} />}
                    <View style={{ flex: 1 }}>
                        <ListToolbar
                            search={{
                                value: search || "",
                                onChange: (val) => {
                                    if (typeof val === 'function') {
                                        setSearch((prev) => val(prev ?? ''));
                                    } else {
                                        setSearch(val || null);
                                    }
                                },
                                onCancel: () => setSearch(null),
                                placeholder: 'Search by product name',
                            }}
                            sort={{
                                selectedId: sortOrder?.id,
                                onSelect: (item) => {
                                    setSortOrder(item as { field: string, order: string, id: number }); toggleOffset();
                                },
                                title: 'Sort by',
                                options: sortOptionsData

                            }}

                            buttons={
                                (!isView) ?
                                    [

                                        {
                                            title: !isAllProduct ? "Add Products" : "Save",
                                            onPress: () => {
                                                const ids = Array.from(selectedProductCard)
                                                !isAllProduct ?
                                                    (navigation.navigate("CategoryProducts", { categoryId, isView, isAllProduct: true, formikValues }))
                                                    :
                                                    DeviceEventEmitter?.emit("CategoryProductsApi", ids)
                                            }
                                        },
                                    ] : []}

                        />
                        {/* </View> */}
                        {(loadPage && !refresh && !footerLoader) ?
                            <ZenLoader lottie={require('../../../assets/infinityLoader.json')} lottieType='Local' viewStyle={{ backgroundColor: "transparent" }} /> :
                            <View style={{ flex: 1 }}>
                                <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", paddingHorizontal: 10, display: selectedProductCard?.size ? "flex" : "none", paddingBottom: 10 }}>
                                    <ZenCheckBox
                                        title={selectedProductCard?.size === listOfProducts?.length ? "Deselect All" : "Select All"}
                                        containerStyle={{ margin: 0, padding: 0, backgroundColor: "transparent", }}
                                        titleStyle={{ fontSize: theme?.mediumFontSize }}
                                        checkedColor={theme.primary}
                                        size={20}
                                        checked={selectedProductCard?.size === listOfProducts?.length}
                                        onPress={() => {
                                            if (selectedProductCard?.size === listOfProducts?.length) {
                                                // Deselect All
                                                setSelectedProductCard(new Set())
                                            }
                                            else {
                                                // Select All
                                                const allIds = new Set<number>()
                                                listOfProducts?.forEach(card =>
                                                    allIds.add(card.id)
                                                )
                                                setSelectedProductCard(allIds)
                                            }
                                        }}
                                    />
                                    {(selectedProductCard.size > 0 && isLandscape && !isAllProduct) &&
                                        <ZenButton
                                            title="Delete"
                                            titleStyle={{ fontSize: theme?.errorFontSize, fontFamily: theme.bold }}
                                            onPress={() => { selectedProductCard?.size > 0 ? setDeletePopover(true) : ZenToast({ type: "info", message: "Choose at least one category to remove" }) }}
                                            buttonStyle={{
                                                backgroundColor: theme?.red,
                                                borderRadius: 10,
                                                padding: 1
                                            }}
                                        />
                                    }

                                </View>
                                <VirtualizedList
                                    keyExtractor={(_: any, index: number) => index?.toString()}
                                    getItemCount={(data: any) => data?.length}
                                    getItem={getItem}
                                    scrollEnabled={true}
                                    nestedScrollEnabled={true}
                                    renderItem={renderItem}
                                    data={listOfProducts || []}
                                    keyboardDismissMode="on-drag"
                                    showsVerticalScrollIndicator={false}
                                    onEndReachedThreshold={0.01}
                                    contentContainerStyle={[{ paddingBottom: listOfProducts.length ? 10 : undefined, flexGrow: listOfProducts?.length ? undefined : 1, marginHorizontal: 10 }]}
                                    ListFooterComponent={footerComponent}
                                    ListFooterComponentStyle={{ height: listOfProducts.length ? 70 : 0 }}
                                    ListEmptyComponent={<ZenEmptyComponent viewStyle={{ backgroundColor: theme?.light, flexGrow: 1 }} />}
                                    onEndReached={onEndReached}
                                    refreshControl={<RefreshControl refreshing={refresh} onRefresh={onRefresh} />}
                                />
                            </View>
                        }
                    </View>
                    {
                        !loadPage && (!isLandscape) && !isAllProduct && !isView && (
                            <View>
                                <View style={CategoryListStyle.plusIconView}>
                                    <ZenIcon
                                        name="plus"
                                        type={"material-community"}
                                        size={30}
                                        color={appColors.light}
                                        onPress={() => {
                                            navigation.navigate("CategoryProducts", { categoryId, isView, isAllProduct: true, formikValues })
                                        }}
                                    />
                                </View>
                            </View>
                        )}
                    <Popover isVisible={deletePopover} popoverStyle={{ width: width > 900 ? 500 : 450, borderRadius: 10 }}>
                        <CommonDialogBox type={selectedProductCard.size > 0 ? 'BULKDELETE' : "DELETE"} replaceValue={selectedProductCard.size > 0 ? 'products' : 'product'} confirmation={confirmation} />
                    </Popover>
                    <CustomModal visible={isAddLoading && isAllProduct} transparent>
                        <ZenLoader transparent viewStyle={{ flex: 1, justifyContent: "center", alignItems: "center", backgroundColor: "transparent" }} color={theme.primary} lottieType="Local" lottie={require('../../../assets/infinityLoader.json')} />
                    </CustomModal>
                </KeyboardAvoidingView >
            )}
        </ThemeConsumer>
    )
}

export default Product






